import { resolveStorageUrl, shortId, type Translate } from '@/entities/moderation';

/**
 * Change-request payloads carry some fields as keyed patch operations
 * (`image_patches`, `modifier_links`): a list of `{key, value: {op, ...}}`
 * entries (the wire also capitalizes it as `{Key, Value}`). Shown raw they
 * read as technical JSON; here they are parsed into entries a reviewer can
 * read — what was added, removed or replaced.
 */
export interface PatchEntry {
  key: string;
  op: string;
  /** `background`, `icon`, … for image patches. */
  slot: string;
  groupId: string;
  imageId: string;
  /** Bare storage key / URL of an uploaded picture. */
  link: string;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function pick(bag: Record<string, unknown>, ...names: string[]): unknown {
  for (const name of names) if (name in bag) return bag[name];
  return undefined;
}

function entryOf(key: string, value: unknown): PatchEntry | null {
  const body = record(value);
  if (!body) return null;
  const op = text(pick(body, 'op', 'Op'));
  const link = text(pick(body, 'link', 'Link'));
  const imageId = text(pick(body, 'image_id', 'imageId'));
  const groupId = text(pick(body, 'group_id', 'groupId'));
  if (op === '' && link === '' && imageId === '' && groupId === '') return null;
  return { key, op, slot: text(pick(body, 'slot', 'Slot')), groupId, imageId, link };
}

/**
 * Parses a patch-operation value into entries, or `null` when the value is
 * not shaped like one (so plain values keep their normal formatting).
 */
export function patchEntriesOf(value: unknown): PatchEntry[] | null {
  const list = Array.isArray(value) ? value : [value];
  const entries: PatchEntry[] = [];
  for (const item of list) {
    const bag = record(item);
    if (!bag) return null;
    const inner = pick(bag, 'value', 'Value');
    if (inner !== undefined) {
      const entry = entryOf(text(pick(bag, 'key', 'Key')), inner);
      if (!entry) return null;
      entries.push(entry);
      continue;
    }
    // A bare `{op, ...}` object or a `{<id>: {op, ...}}` map.
    const direct = entryOf('', bag);
    if (direct) {
      entries.push(direct);
      continue;
    }
    const mapped = Object.entries(bag).map(([key, body]) => entryOf(key, body));
    if (mapped.length === 0 || mapped.some((entry) => entry === null)) return null;
    entries.push(...(mapped as PatchEntry[]));
  }
  return entries.length > 0 ? entries : null;
}

const PATCH_FIELD = /(?:^|_)(?:patches|links)$/;

/**
 * Patch entries of a field, when the field is one that carries operations:
 * named like `image_patches` / `modifier_links`, or any value whose entries
 * all state an `op`. Plain galleries (`images: [{url}]`) stay untouched.
 */
export function patchFieldEntries(key: string, value: unknown): PatchEntry[] | null {
  const entries = patchEntriesOf(value);
  if (!entries) return null;
  return PATCH_FIELD.test(key) || entries.every((entry) => entry.op !== '')
    ? entries
    : null;
}

const DELETE_OPS = new Set(['delete', 'remove', 'detach', 'unlink']);
const ADD_OPS = new Set(['add', 'attach', 'link', 'create', 'upload', 'set', 'put']);

export function isRemovalOp(op: string): boolean {
  return DELETE_OPS.has(op.toLowerCase());
}

export function isAdditionOp(op: string): boolean {
  return ADD_OPS.has(op.toLowerCase());
}

/** Picture behind an entry, resolved to a displayable URL (`''` when none). */
export function patchImageUrl(entry: PatchEntry): string {
  return resolveStorageUrl(entry.link);
}

function slotLabel(slot: string, t: Translate): string {
  switch (slot.toLowerCase()) {
    case 'background':
      return t('review.patch.slotBackground', 'фоновое изображение');
    case 'icon':
    case 'logo':
      return t('review.patch.slotIcon', 'логотип');
    case 'main':
      return t('review.patch.slotMain', 'главное фото');
    default:
      return t('review.patch.slotImage', 'изображение');
  }
}

/**
 * One readable line for an entry. `groupName` resolves a modifier group id to
 * its name (the caller knows the product's groups); an unknown id falls back
 * to the short id rather than the raw UUID.
 */
export function describePatchEntry(
  entry: PatchEntry,
  t: Translate,
  groupName?: (id: string) => string | undefined,
): string {
  if (entry.groupId !== '') {
    const name = groupName?.(entry.groupId) ?? shortId(entry.groupId);
    if (isRemovalOp(entry.op)) {
      return t('review.patch.modifierDetached', 'Модификатор «{{name}}» убран').replace(
        '{{name}}',
        name,
      );
    }
    if (isAdditionOp(entry.op)) {
      return t(
        'review.patch.modifierAttached',
        'Модификатор «{{name}}» добавлен',
      ).replace('{{name}}', name);
    }
    return t('review.patch.modifierChanged', 'Модификатор «{{name}}» изменён').replace(
      '{{name}}',
      name,
    );
  }
  const slot = slotLabel(entry.slot, t);
  if (isRemovalOp(entry.op)) {
    return t('review.patch.imageRemoved', 'Удалено: {{slot}}').replace('{{slot}}', slot);
  }
  if (entry.link !== '' || entry.imageId !== '' || isAdditionOp(entry.op)) {
    return t('review.patch.imageAdded', 'Добавлено: {{slot}}').replace('{{slot}}', slot);
  }
  return t('review.patch.imageChanged', 'Изменено: {{slot}}').replace('{{slot}}', slot);
}
